import { useEffect, useState } from 'react';

/**
 * Hook React minimaliste : retourne `true` quand la media query CSS matche.
 *
 * Usage :
 *   const isMobile = useMediaQuery('(max-width: 768px)');
 *
 * Ré-évalué automatiquement à chaque changement (resize, rotation device).
 * Safe-by-default côté SSR : renvoie `false` si `window` est absent.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // addEventListener moderne ; fallback addListener pour vieux Safari
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    setMatches(mql.matches); // sync initial après mount
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return matches;
}
